/**
 * demo-role-switcher.tsx — Floating button on every authenticated
 * screen that lets a demo-tenant visitor hop between accounts
 * without re-entering credentials.
 *
 * Renders ONLY when the active user's tenantSubdomain is the
 * literal demo constant. On every other tenant — production or
 * not — the component returns null and disappears from the tree.
 *
 * Behaviour:
 *   - Closed state: a small floating "Switch role" button in the
 *     lower-trailing corner.
 *   - Open state: a popover listing every demo account with their
 *     role label and a subtle "current" indicator next to the
 *     active session.
 *   - Click → POST /demo/switch-account → replace auth store with
 *     the fresh token pair → navigate to the new user's
 *     role-appropriate home route (pickHomeRoute).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Loader2, UserCog, X } from 'lucide-react';
import { useAuthStore } from '@/lib/auth-store';
import { demoApi, type DemoAccountSummary } from '@/lib/api/demo';
import { Button } from '@/components/ui/button';
import { pickHomeRoute } from '@/types/auth';

const DEMO_SUBDOMAIN = 'imtithal-demo';
const DEMO_EMAIL_DOMAIN = `@${DEMO_SUBDOMAIN}.sa`;

export function DemoRoleSwitcher(): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const locale = i18n.language === 'ar' ? 'ar' : 'en';

  // Hard gate — anything but the demo tenant hides the switcher.
  // Two signals checked: (1) the canonical `tenantSubdomain` field
  // from the backend's user DTO, and (2) the user's email suffix.
  // The email fallback covers sessions persisted in localStorage
  // BEFORE the backend started returning tenantSubdomain — a stale
  // session would otherwise hide the switcher until the user logs
  // out and back in. The backend still validates every demo call
  // against the literal subdomain server-side, so the fallback is
  // a UX convenience, not a security relaxation.
  const isDemoTenant =
    user?.tenantSubdomain === DEMO_SUBDOMAIN ||
    !!user?.email?.toLowerCase().endsWith(DEMO_EMAIL_DOMAIN);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const accountsQuery = useQuery({
    queryKey: ['demo-accounts'],
    queryFn: demoApi.listAccounts,
    enabled: isDemoTenant && open,
    staleTime: 60_000,
  });

  const switchMutation = useMutation({
    mutationFn: (emailLocal: string) => demoApi.switchAccount(emailLocal),
    onSuccess: (data) => {
      setSession({
        user: data.user,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresInSeconds: data.expiresIn,
      });
      setOpen(false);
      setErrorMsg(null);
      navigate(pickHomeRoute(data.user), { replace: true });
    },
    onError: (err) => {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    },
  });

  if (!isDemoTenant) return null;

  return (
    <div className="fixed bottom-6 end-6 z-50">
      {open ? (
        <div
          ref={popoverRef}
          className="mb-2 w-80 overflow-hidden rounded-lg border bg-white shadow-xl"
        >
          <div className="flex items-center justify-between border-b bg-amber-50 px-4 py-2">
            <div className="text-sm font-semibold text-amber-900">
              {t('demoSwitcher.title')}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-amber-700 hover:text-amber-900"
              aria-label={t('common.close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="border-b px-4 py-2 text-xs text-muted-foreground">
            {t('demoSwitcher.subtitle')}
          </div>

          {errorMsg ? (
            <div className="border-b bg-rose-50 px-4 py-2 text-xs text-rose-700">
              {errorMsg}
            </div>
          ) : null}

          <div className="max-h-96 overflow-y-auto">
            {accountsQuery.isLoading ? (
              <div className="flex items-center justify-center px-4 py-6 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : accountsQuery.isError ? (
              <div className="px-4 py-4 text-sm text-rose-700">
                {t('demoSwitcher.loadError')}
              </div>
            ) : (
              (accountsQuery.data ?? []).map((account: DemoAccountSummary) => {
                const isCurrent = account.email === user?.email;
                const isPending =
                  switchMutation.isPending && switchMutation.variables === account.emailLocal;
                return (
                  <button
                    key={account.emailLocal}
                    type="button"
                    disabled={isCurrent || switchMutation.isPending}
                    onClick={() => {
                      setErrorMsg(null);
                      switchMutation.mutate(account.emailLocal);
                    }}
                    className="flex w-full items-start justify-between gap-3 border-b px-4 py-3 text-start text-sm transition hover:bg-slate-50 disabled:opacity-60"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-slate-900">
                        {account.firstName} {account.lastName}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {locale === 'ar' ? account.roleNameAr : account.roleNameEn}
                      </div>
                      <div className="truncate text-[11px] text-muted-foreground">
                        {account.email}
                      </div>
                    </div>
                    <div className="shrink-0 pt-1">
                      {isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                      ) : isCurrent ? (
                        <Check className="h-4 w-4 text-emerald-600" />
                      ) : null}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}

      <Button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="gap-2 rounded-full bg-amber-500 px-4 text-white shadow-lg hover:bg-amber-600"
      >
        <UserCog className="h-4 w-4" />
        {t('demoSwitcher.openCta')}
      </Button>
    </div>
  );
}
